import { BusinessDayCalculatorPL, CommunityTransactionForSaleMixin, CommunityTransactionForSaleQuery, CompanyParametersQuery, ContextKeys, DocumentExchangeRateValidationBoPart, DocumentItemQuantityGreaterThanInventoryLevelError, DocumentItemQuantityGreaterThanInventoryLevelWarning, DocumentItemsNumberingBoPart, DocumentNumberBoPart, DocumentVatRateForSaleValidationBoPart, DocumentWstoBoPart, ExchangeRateMixin, InventoryQuery, NumberingQuery, ProductInventoryLevelQuery, propertyChangedJoinPointFilter, rewriteAddress, rewriteDocumentContractor, rewriteDocumentItem, rewriteExchangeRate, SaleAdvanceRepositoryBase, SaleDocumentAdjustmentRepositoryBase, SaleDocumentRepositoryBase, VatRateForPurchaseQuery, VatRateForSaleQuery } from '@skylab-shared/business';
import { BO, BusinessObjectBase, PropertyChangedJoinPoint, QueryBus, ResponseConflictObject, Weaver } from '@skylab-shared/core';
import { AddressModel, CalculationMethod, ExchangeRateModel, InventoryDocumentItemModel, InventoryDocumentKind, InventoryGoodsIssueDocumentModel, InventoryRelationshipCreationMethod, RelatedEntity, SaleDocumentKind } from '@skylab-shared/dataModel';
import { head, not, substractDays } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { InventoryDocumentCalculatorBoPart } from './bo-parts/inventory-document-calculator.bo-part';
import { InventoryDocumentItemsValidationsBoPart } from './bo-parts/inventory-document-items-validations.bo-part';
import { InventoryDocumentErrorCodes, RequestedQuantityOfProductNotPresentInInventoryErrorObject } from './common/inventory-document-error-codes';
import { InventoryLinksMixin } from './mixins/inventory-links.mixin';
import { refreshInventoryIssueDocumentItemPriceAsync } from './utils/inventory-document-item-utils';

export interface InventoryGoodsIssueDocumentBO extends InventoryLinksMixin<InventoryGoodsIssueDocumentModel>, ExchangeRateMixin<InventoryGoodsIssueDocumentModel>, CommunityTransactionForSaleMixin<InventoryGoodsIssueDocumentModel> {};

@BO({
    modelType: InventoryGoodsIssueDocumentModel,
    mixins: [InventoryLinksMixin, ExchangeRateMixin, CommunityTransactionForSaleMixin]
})
export class InventoryGoodsIssueDocumentBO extends BusinessObjectBase<InventoryGoodsIssueDocumentModel> {
    private itemsQuantityOnOriginalDocument!: Map<InventoryDocumentItemModel, number>;

    public constructor(
        weaver: Weaver,
        protected readonly productRepository: ProductRepository,
        protected readonly queryBus: QueryBus,
        protected readonly saleDocumentRepository: SaleDocumentRepositoryBase,
        protected readonly saleDocumentAdjustmentRepository: SaleDocumentAdjustmentRepositoryBase,
        protected readonly saleAdvanceRepository: SaleAdvanceRepositoryBase
    ) {
        super(weaver);
        this.initBOParts();
    }

    protected override handleConflictSaveResponse(conflict: ResponseConflictObject<InventoryDocumentErrorCodes, RequestedQuantityOfProductNotPresentInInventoryErrorObject>) {
        if ((conflict.code === InventoryDocumentErrorCodes.RequestedQuantityOfProductIsNotAvailable)) {
            const productIds = conflict.objectProperties?.filter((elem): elem is RequestedQuantityOfProductNotPresentInInventoryErrorObject => typeof elem === 'object').map(elem => elem.productId);

            this.model.items.forEach(elem => {
                if (!elem.productId || !productIds?.includes(elem.productId)) return;
                this.addExternalRuleViolations<InventoryDocumentItemModel>({
                    [elem.sun]: [{
                        property: 'quantity',
                        violation: new DocumentItemQuantityGreaterThanInventoryLevelError()
                    }]
                });
                this.validation.queueValidationByViolationType(DocumentItemQuantityGreaterThanInventoryLevelWarning);
            });
        }
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.all([
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(VatRateForPurchaseQuery),
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(VatRateForSaleQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(InventoryQuery),
            this.localContext.preload(CommunityTransactionForSaleQuery)
        ]);

        if (this.model.isPersistent) {
            await this.loadLinksAsync();
            this.itemsQuantityOnOriginalDocument = new Map(this.model.items.map(item => [item, item.quantity]));
            this.isGoodsIssueAssociatedWithSaleDocument = !!this.linksCommercial;
        }

        this.registerDocumentReadonlyWhenAssociatedToSaleDocumentRule();
        this.registerItemRules();

        this.registerServerRuleViolationAfterGoodsIssueDateChange();
    }

    private initBOParts(): void {
        this.parts.push(...[
            new DocumentNumberBoPart(this.weaver, this.queryBus),
            new InventoryDocumentCalculatorBoPart(this.weaver),
            new DocumentItemsNumberingBoPart(this.weaver),
            new InventoryDocumentItemsValidationsBoPart(this.weaver),
            new DocumentExchangeRateValidationBoPart(this.weaver),
            new DocumentWstoBoPart(this.weaver, this.queryBus),
            new DocumentVatRateForSaleValidationBoPart(this.weaver)
        ]);
    }

    private registerServerRuleViolationAfterGoodsIssueDateChange(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(InventoryGoodsIssueDocumentModel, ['goodsIssueDate'])(
            () => this.removeServerRuleViolations()
        )));
    }

    private registerDocumentReadonlyWhenAssociatedToSaleDocumentRule(): void {
        const modelType = InventoryGoodsIssueDocumentModel;

        this.readonly.addRule([
            [modelType, 'calculationMethod'],
            [modelType, 'currencyId'],
            [modelType, 'inventoryId'],
            [modelType, 'issueDate'],
            [modelType, 'issuer'],
            [modelType, 'goodsIssueDate'],
            [modelType, 'recipient'],
            [modelType, 'communityTransactionId'],
            [modelType, 'client'],
            [modelType, 'transactionLocation'],
            [modelType, 'wstoAdditionalInformation'],
            [modelType, 'wstoConsumerCountryId'],
            [modelType, 'wstoShipmentCountryId'],
            [modelType, 'numberingId'],
            [modelType, 'documentNumber']
        ], () => this.isAssociatedDocument);

        this.readonly.setReadonlyOnSubtree(InventoryDocumentItemModel, () => this.isAssociatedDocument);
        this.readonly.setReadonlyOnSubtree(ExchangeRateModel, () => this.isAssociatedDocument);
        this.readonly.setReadonlyOnSubtree(AddressModel, () => this.isAssociatedDocument);
    }

    public get isAssociatedDocument(): boolean {
        return this.isGoodsIssueAssociatedWithSaleDocument || this.isDocumentAdjusted || this.isDocumentAssociatedWithStocktaking;
    }

    private registerItemRules(): void {
        this.validation.addRule(
            [[InventoryDocumentItemModel, 'quantity']],
            async ctx => {
                if (this.validationResult.find(elem => (elem.ruleViolation.violation instanceof DocumentItemQuantityGreaterThanInventoryLevelError)
                    && elem.source === ctx.source)) return true;

                const originalItemQuantity = this.itemsQuantityOnOriginalDocument?.get(ctx.source);
                if (originalItemQuantity === ctx.source.quantity) return true;

                const inventoryLevel = ctx.source.inventoryLevel ?? await this.getItemInventoryLevel(ctx.source);
                const inventoryLevelAfterUpdate = originalItemQuantity ? (inventoryLevel + this.itemsQuantityOnOriginalDocument.get(ctx.source)!) : inventoryLevel;

                return not(ctx.source.quantity > inventoryLevelAfterUpdate);
            },
            DocumentItemQuantityGreaterThanInventoryLevelWarning
        );

        this.readonly.addRule([
            [InventoryDocumentItemModel, 'productName'],
            [InventoryDocumentItemModel, 'unitId']
        ], () => true);
    }

    private async getItemInventoryLevel(item: InventoryDocumentItemModel): Promise<number> {
        const productInventoryData = await this.localContext.getAsync(ContextKeys.productInventoryLevel(item.productId!), () => this.queryBus.queryAsync(new ProductInventoryLevelQuery(item.productId!)));
        return productInventoryData?.quantity ?? 0;
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = substractDays(this.model.goodsIssueDate, 1);
        return BusinessDayCalculatorPL.getLastBusinessDay(date);
    }

    public isGoodsIssueAssociatedWithSaleDocument: boolean;

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => await refreshInventoryIssueDocumentItemPriceAsync(item, this.localContext, this.model, this.productRepository)));
    }

    public async fillGoodsIssueToInvoiceAsync(saleDocumentKind: SaleDocumentKind.AdvanceInvoice | SaleDocumentKind.InvoiceAdjustment | SaleDocumentKind.ReceiptAdjustment, saleDocumentId: number, creationMethod: InventoryRelationshipCreationMethod): Promise<void> {
        const source = await (this.getSaleRepositoryForKind(saleDocumentKind)).findAsync(saleDocumentId);

        this.isGoodsIssueAssociatedWithSaleDocument = true;

        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery);
        const vatRates = this.localContext.getPreloaded(VatRateForSaleQuery);
        const numberings = this.localContext.getPreloaded(NumberingQuery);
        const inventory = head(this.localContext.getPreloaded(InventoryQuery));
        const communityTransactions = this.localContext.getPreloaded(CommunityTransactionForSaleQuery);
        const defaultCommunityTransaction = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { default: true });
        const wasWstoTransaction = (source.communityTransactionId === communityTransactions.find(elem => elem.symbol === 'WSTO-OSS')?.id)
            && !companyParameters.ossRegistered;

        this.model.kind = InventoryDocumentKind.GoodsIssue;
        this.model.issuedByVatPayer = companyParameters.vatPayer;
        this.model.inventoryId = inventory.id;
        this.model.relatedCommercialDocuments = [RelatedEntity.from(source)];

        rewriteDocumentContractor(this.model, source)

        this.model.calculationMethod = this.model.issuedByVatPayer ? source.calculationMethod as CalculationMethod.Gross | CalculationMethod.Net : CalculationMethod.Gross;
        this.model.communityTransactionId = !this.model.issuedByVatPayer || wasWstoTransaction ? defaultCommunityTransaction.id : source.communityTransactionId;
        this.model.currencyId = source.currencyId;

        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.includes(this.model.kind));
        this.model.numberingId = defaultNumberingId;

        const inventoryDate = source.goodsIssueDate || source.saleDate!;
        this.model.issueDate = new Date(inventoryDate);
        this.model.goodsIssueDate = new Date(inventoryDate);

        this.model.transactionLocation = source.transactionLocation;
        this.model.recipient = source.recipient;
        this.model.issuer = source.issuer;

        this.model.wstoAdditionalInformation = source.wstoAdditionalInformation;
        this.model.wstoConsumerCountryId = source.wstoConsumerCountryId;
        this.model.wstoShipmentCountryId = source.wstoShipmentCountryId;
        this.model.creationMethod = creationMethod;

        source.items.filter(elem => elem.includesInventoryControl).map(item => {
            const model = new InventoryDocumentItemModel();
            this.model.items.push(model);
            rewriteDocumentItem(model, item);
            model.vatRateId = (this.model.issuedByVatPayer
                ? wasWstoTransaction ? vatRates.find(elem => elem.default)?.id : item.vatRateId
                : vatRates.find(elem => elem.exempt)?.id)!;
            model.relatedCommercialItemsIds = item.id ? [item.id] : [];
        });

        if (source.exchangeRate) {
            const exchangeRate = new ExchangeRateModel();
            this.model.exchangeRate = exchangeRate;
            rewriteExchangeRate(exchangeRate, source.exchangeRate);
        }

        if (source.deliveryAddress) {
            const deliveryAddress = new AddressModel();
            this.model.deliveryAddress = deliveryAddress;
            rewriteAddress(deliveryAddress, source.deliveryAddress);
        }
    }

    private getSaleRepositoryForKind(kind: SaleDocumentKind.AdvanceInvoice | SaleDocumentKind.InvoiceAdjustment | SaleDocumentKind.ReceiptAdjustment): SaleDocumentRepositoryBase | SaleDocumentAdjustmentRepositoryBase | SaleAdvanceRepositoryBase {
        return {
            [SaleDocumentKind.AdvanceInvoice]: this.saleAdvanceRepository,
            [SaleDocumentKind.InvoiceAdjustment]: this.saleDocumentAdjustmentRepository,
            [SaleDocumentKind.ReceiptAdjustment]: this.saleDocumentAdjustmentRepository
        }[kind] || this.saleDocumentRepository;
    }
}
